import React, { useState } from 'react';
import { useUser } from '../contexts/UserContext';
import QuestRescheduleModal from './QuestRescheduleModal';

const PenaltyBanner: React.FC = () => {
  const { profile, quests, rescheduleQuest, isDemoMode } = useUser();
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedQuest, setSelectedQuest] = useState<any>(null);

  // Don't show penalties in demo mode
  if (isDemoMode || !profile?.penaltyActive) return null;

  const penaltyQuests = quests.filter(q => 
    profile.penaltyQuestIds.includes(q.id)
  );

  return (
    <div 
      className="relative mb-6 overflow-hidden rounded-xl border border-red-500/30 shadow-2xl"
      style={{
        backgroundColor: '#a0a0a015',
        WebkitBackdropFilter: 'blur(10px)',
        backdropFilter: 'blur(10px)',
      }}
    >
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-red-950/20 via-red-900/20 to-red-950/20" />
      
      {/* Hexagonal Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l25.98 15v30L30 60 4.02 45V15z' fill='none' stroke='%23fff' stroke-width='1'/%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px',
        }}
      />
      
      {/* Border Glow Animation */}
      <div className="absolute inset-0 border-2 border-red-500/30 animate-pulse pointer-events-none" />
      
      {/* Content */}
      <div className="relative z-10 px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-start gap-3 sm:gap-4">
          {/* Warning Icon */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-red-500/20 flex items-center justify-center animate-glow-pulse">
              <span className="text-xl sm:text-2xl">⚠️</span>
            </div>
          </div>
          
          {/* Message Content */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg sm:text-xl font-orbitron font-black text-red-500 uppercase tracking-wider drop-shadow-[0_0_10px_rgba(239,68,68,0.6)]">
                PENALTY QUEST ACTIVE
              </h3>
            </div>
            
            <p className="text-gray-300 font-rajdhani text-sm sm:text-base mb-3">
              You have overdue quests. <span className="text-red-400 font-bold">XP gains are blocked</span> until you complete the following:
            </p>
            
            {/* Penalty Quests List */}
            <div className="space-y-0">
              {penaltyQuests.map(quest => {
                // Determine penalty reason
                let reason = '';
                if (quest.resetDaily) {
                  reason = 'Missed Daily';
                } else if (quest.scheduledFor) {
                  reason = 'Past Due Date';
                } else {
                  reason = 'Not Completed';
                }
                
                // Badge styles
                const reasonBadgeClasses =
                  reason === 'Missed Daily'
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                    : reason === 'Past Due Date'
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    : 'bg-gray-500/20 text-gray-300 border border-gray-500/30';
                const incompleteBadgeClasses = 'bg-red-500/20 text-red-400 border border-red-500/30';
                
                return (
                  <div 
                    key={quest.id}
                    className="bg-black/30 border border-red-500/30 border-b-red-500/20 rounded px-2 sm:px-3 py-1.5 flex items-center justify-between gap-3"
                  >
                    {/* Quest Details (Left) */}
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-red-400 text-lg flex-shrink-0">✕</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-orbitron font-bold text-white text-sm">{quest.title}</div>
                          <span className={`text-xs px-2 py-1 sm:px-1.5 sm:py-0.5 rounded font-rajdhani font-bold min-h-[24px] sm:min-h-0 flex items-center ${reasonBadgeClasses}`}>
                            {reason}
                          </span>
                          {quest.status === 'active' && (
                            <span className={`text-xs px-2 py-1 sm:px-1.5 sm:py-0.5 rounded font-rajdhani font-bold min-h-[24px] sm:min-h-0 flex items-center ${incompleteBadgeClasses}`}>
                              Incomplete
                            </span>
                          )}
                        </div>
                        {quest.description && (
                          <div className="text-xs text-gray-400 font-rajdhani mt-0.5">{quest.description}</div>
                        )}
                      </div>
                    </div>
                    
                    {/* Reschedule Button (Right) */}
                    <button
                      onClick={() => {
                        setSelectedQuest(quest);
                        setShowRescheduleModal(true);
                      }}
                      className="px-3 py-2 sm:py-1.5 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-500 hover:to-orange-600 border border-orange-500/50 text-orange-100 text-xs font-orbitron font-bold rounded transition-all shadow-[0_0_10px_rgba(251,146,60,0.3)] hover:shadow-[0_0_15px_rgba(251,146,60,0.5)] min-h-[36px] sm:min-h-[32px] touch-manipulation flex-shrink-0"
                      title="Reschedule quest"
                    >
                      📅 Reschedule
                    </button>
                  </div>
                );
              })}
            </div>
            
            {/* System Quote */}
            <div className="mt-3 pl-2 sm:pl-4 border-l-2 border-red-500/30">
              <p className="text-xs sm:text-sm font-rajdhani text-gray-500 italic">
                "The System does not forgive failure. Complete the penalty quest to restore your XP gains."
              </p>
            </div>
          </div>
          
          {/* Pulsing Alert Line */}
          <div className="flex-shrink-0">
            <div className="w-1 h-full bg-red-500 animate-pulse rounded-full" />
          </div>
        </div>
      </div>
      
      {/* Bottom Glow Line */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-red-500 to-transparent animate-pulse" />
      
      {/* Reschedule Modal */}
      {selectedQuest && (
        <QuestRescheduleModal
          quest={selectedQuest}
          isOpen={showRescheduleModal}
          onClose={() => {
            setShowRescheduleModal(false);
            setSelectedQuest(null);
          }}
          onReschedule={(questId, newDate) => {
            rescheduleQuest(questId, newDate);
            setShowRescheduleModal(false);
            setSelectedQuest(null);
          }}
        />
      )}
    </div>
  );
};

export default PenaltyBanner;

